import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ProviderManager from '../../../provider/providerManager';
import signalRService from "app/utilities/signalR/SignalRService";
import { SignalRConstants } from "app/utilities/signalR/SignalRConstants";
import { StepsEnum } from './Enums/StepsEnum';
import { ErrorsEnum } from './Enums/ErrorsEnum';
import { TroubleshootingStepEnum } from './Enums/TroubleshootingStepEnum';
import {scrollToTop} from "app/Components/Block/AbroadTroubleshootingWizardBlock/Utils/ContentDataUtils";

export const startTroubleshootingWizard = createAsyncThunk(
  'TroubleshootingWizard/StartTroubleshootingWizard',
  async (data, thunkAPI) => {
    try {
      const { selectedSubscriber, process, processCheckPoint, clientRequestData } = thunkAPI.getState().TroubleshootingWizardSlice;

      console.log('Starting troubleshooting wizard for subscriber:', selectedSubscriber);

      // Enhanced SignalR event callbacks with all notification types
      const eventCallbacks = {
        //Update event
        'Update': (updateData) => {
          console.log('Update event received:', updateData);
          if (updateData.step) {
            thunkAPI.dispatch(setTroubleshootingStep(updateData.step));
          }
        },
      };

      // Setup SignalR connection for troubleshooting group
      console.log('Setting up SignalR connection...');
      await signalRService.setupConnection(
        selectedSubscriber,
        SignalRConstants.Groups.TROUBLESHOOTING,
        eventCallbacks
      );

      // Verify connection before proceeding
      if (!signalRService.isConnected()) {
        throw new Error('Failed to establish SignalR connection');
      }

      console.log('SignalR connection established successfully, starting wizard...');

      // Create proper payload object
      const payload = {
        subscriberNumber: selectedSubscriber,
        process: process
      };

      // Add checkpoint and clientRequestData if they exist (for resume functionality)
      if (processCheckPoint) {
        payload.checkpoint = processCheckPoint;
      }

      if (clientRequestData) {
        payload.clientRequestData = clientRequestData;
      }

      console.log('Sending troubleshooting wizard request with payload:', payload);
      const result = await ProviderManager.getProviderData('startTroubleshootingWizard', payload);

      console.log('Troubleshooting wizard API response:', result);
      return result;

    } catch (error) {
      console.error('Error in startTroubleshootingWizard:', error);

      // Attempt to clean up SignalR connection on error
      try {
        await signalRService.stopConnection();
      } catch (cleanupError) {
        console.error('Error cleaning up SignalR connection:', cleanupError);
      }

      return thunkAPI.rejectWithValue({
        error: true,
        message: error.message || 'Unknown error occurred',
        timestamp: new Date().toISOString()
      });
    }
  }
);

// New async thunk for cleaning up SignalR connection
export const cleanupTroubleshootingConnection = createAsyncThunk(
  'TroubleshootingWizard/CleanupConnection',
  async (_, thunkAPI) => {
    try {
      const { selectedSubscriber } = thunkAPI.getState().TroubleshootingWizardSlice;

      if (selectedSubscriber) {
        await signalRService.leaveTroubleshootingGroup(selectedSubscriber);
      }

      await signalRService.stopConnection();
      console.log('SignalR connection cleaned up successfully');

      return { success: true };
    } catch (error) {
      console.error('Error cleaning up SignalR connection:', error);
      return { success: false, error: error.message };
    }
  }
);

const initialState = {
  // Original state
  step: StepsEnum.SELECT_SUBSCRIBER,
  subscriberList: null,
  selectedSubscriber: null,
  errorReason: null,
  process: null,
  troubleshootingStep: null,
  selectedListResult: null,
  processCheckPoint: null,
  clientRequestData: null,

  // Enhanced SignalR state
  connectionStatus: 'disconnected', // 'disconnected', 'connecting', 'connected', 'error'
  connectionError: null,

  // Real-time updates
  progressUpdate: null,
  statusUpdate: null,
  errorNotification: null,
  processCompletion: null,
  statusMessage: null,

  // Loading states
  isLoading: false,
  loadingMessage: null
};

const TroubleshootingWizardSlice = createSlice({
  name: 'TroubleshootingWizardSlice',
  initialState: initialState,
  reducers: {
    resetState: (state) => {
      // Clean up SignalR connection when resetting state
      signalRService.stopConnection().catch(error => {
        console.error('Error stopping SignalR connection during reset:', error);
      });
      return { ...initialState };
    },

    setStep: (state, action) => {
      state.step = action.payload;
      scrollToTop();
    },

    setSelectedSubscriber: (state, action) => {
      state.selectedSubscriber = action.payload;
    },

    setError: (state, action) => {
      state.errorReason = action.payload;
    },

    setTroubleshootingStep: (state, action) => {
      scrollToTop();
      state.troubleshootingStep = action.payload;
    },

    saveToHistory: (state) => {
      return state;
    },

    setProcess: (state, action) => {
      state.process = action.payload;
    },

    setSelectedListResult: (state, action) => {
      if (action.payload) {
        state.selectedListResult = action.payload;
      } else {
        state.selectedListResult = null;
      }
    },

    submitListResult: (state) => {
      scrollToTop();
      switch (state.troubleshootingStep) {
        case TroubleshootingStepEnum.RESTART_DEVICE:
          state.troubleshootingStep = TroubleshootingStepEnum.REFRESH_SETTINGS_CONFIRMATION;
          break;

        case TroubleshootingStepEnum.REFRESH_SETTINGS_CONFIRMATION:
          if (state.selectedListResult) {
            state.troubleshootingStep = TroubleshootingStepEnum.RESOLVED_ROAMING_SWITCH_BLOCK;
          } else {
            state.step = StepsEnum.ERROR;
            state.errorReason = ErrorsEnum.UNRESOLVED_ISSUE;
          }
          break;

        case TroubleshootingStepEnum.IS_SUBSCRIBER_IN_JORDAN_OR_EGYPT:
          if (state.selectedListResult) {
            state.troubleshootingStep = TroubleshootingStepEnum.RESOLVED_ROAMING_SWITCH_BLOCK;
          } else {
            state.step = StepsEnum.ERROR;
            state.errorReason = ErrorsEnum.UNRESOLVED_ISSUE;
          }
          break;
      }
    },

    setProcessCheckpoint: (state, action) => {
      if (action.payload) {
        state.processCheckPoint = action.payload;
      } else {
        state.processCheckPoint = null;
      }
    },

    setClientRequestData: (state, action) => {
      if (action.payload) {
        state.clientRequestData = action.payload;
      } else {
        state.clientRequestData = null;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // startTroubleshootingWizard
      .addCase(startTroubleshootingWizard.pending, (state, action) => {
        scrollToTop();
        state.step = StepsEnum.TROUBLESHOOT;
        state.troubleshootingStep = TroubleshootingStepEnum.MUTUAL_CHECKS;
        state.isLoading = true;
        state.loadingMessage = 'מתחיל אשף פתרון בעיות...';
        state.connectionStatus = 'connecting';
        state.errorNotification = null;
        state.processCompletion = null;
      })
      .addCase(startTroubleshootingWizard.fulfilled, (state, action) => {
        const initResult = action.payload?.Body;
        scrollToTop();

        if (!initResult?.isSuccess) {
          state.step = StepsEnum.ERROR;
          state.errorReason = initResult?.resultCode ?? ErrorsEnum.GENERAL_ERROR;
          state.isLoading = false;
          state.loadingMessage = null;
          return;
        }

        state.step = StepsEnum.TROUBLESHOOT;
        state.troubleshootingStep = TroubleshootingStepEnum[initResult.resultCode] || TroubleshootingStepEnum.MUTUAL_CHECKS;
        state.connectionStatus = 'connected';

        // If the wizard completed immediately, stop loading
        if (initResult.resultCode === 'NO_ERROR' || initResult.resultCode === 'SUCCESS') {
          state.isLoading = false;
          state.loadingMessage = null;
        }
      })
      .addCase(startTroubleshootingWizard.rejected, (state, action) => {
        scrollToTop();
        state.step = StepsEnum.ERROR;
        state.errorReason = ErrorsEnum.GENERAL_ERROR;
        state.isLoading = false;
        state.loadingMessage = null;
        state.connectionStatus = 'error';
        state.connectionError = action.payload?.message || 'Failed to start troubleshooting wizard';
      })

      // cleanupTroubleshootingConnection
      .addCase(cleanupTroubleshootingConnection.fulfilled, (state, action) => {
        state.connectionStatus = 'disconnected';
        state.connectionError = null;
        state.progressUpdate = null;
        state.statusUpdate = null;
        state.errorNotification = null;
        state.processCompletion = null;
      })
      .addCase(cleanupTroubleshootingConnection.rejected, (state, action) => {
        console.error('Failed to cleanup SignalR connection:', action.payload);
        // Still mark as disconnected even if cleanup failed
        state.connectionStatus = 'disconnected';
      });
  }
});

export const {
  resetState,
  setStep,
  setSelectedSubscriber,
  setError,
  saveToHistory,
  setProcess,
  setTroubleshootingStep,
  setSelectedListResult,
  submitListResult,
  setProcessCheckpoint,
  setClientRequestData,
  setConnectionStatus,
  setConnectionError,
  setProgressUpdate,
  setStatusUpdate,
  setErrorNotification,
  setProcessCompletion,
  setStatusMessage,
  clearNotifications,
} = TroubleshootingWizardSlice.actions;

export default TroubleshootingWizardSlice.reducer;
